import { DEFAULT_LANGUAGE, User, PublicUser } from '@cibli/shared';
import * as Sentry from '@sentry/react';
import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import type { UserInfo } from '@/types/user-info';

import { useAuth } from '@/contexts/auth.context';
import { signOut } from '@/lib/auth-client';
import { setApiLanguage } from '@/services/api';
import { clearOtpCache } from '@/services/otp';
import { createUser, getCurrentUser } from '@/services/user';

export type AppUser = User | PublicUser;

interface UserContextState {
  user: AppUser | null;
  login: (userInfos: UserInfo) => Promise<boolean>;
  loginWithExistingUser: () => Promise<boolean>;
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
}

const UserContext = createContext<UserContextState | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

/**
 * Provides the dashboard app user (from /users/me) and auth actions (login, logout).
 * Depends on AuthProvider (better-auth session) for refetch on logout.
 */
export function UserProvider({ children }: UserProviderProps) {
  const [user, setUser] = useState<AppUser | null>(null);
  const { refetch: refetchSession } = useAuth();

  const setUserSafe = useCallback((u: AppUser | null) => {
    setUser(u as User | null);
    Sentry.setUser(
      u
        ? {
            id: u.id,
            email: 'email' in u && u.email ? String(u.email) : undefined,
          }
        : null,
    );
  }, []);

  const login = useCallback(
    async (userInfos: UserInfo): Promise<boolean> => {
      try {
        const createdUser = await createUser(userInfos, { showErrorToast: false });
        setUserSafe(createdUser);
        return true;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [setUserSafe],
  );

  const loginWithExistingUser = useCallback(async (): Promise<boolean> => {
    try {
      const existingUser = await getCurrentUser({ showErrorToast: false });
      if (!existingUser?.id) {
        throw new Error('No user returned from /users/me');
      }
      setUserSafe(existingUser);
      return true;
    } catch (error) {
      console.error('Failed to login with existing user:', error);
      return false;
    }
  }, [setUserSafe]);

  const refreshUser = useCallback(async () => {
    try {
      const u = await getCurrentUser({ showErrorToast: false });
      if (u?.id) setUserSafe(u);
    } catch {
      // ignore
    }
  }, [setUserSafe]);

  const logout = useCallback(async () => {
    setApiLanguage(DEFAULT_LANGUAGE);

    try {
      await signOut();
      await refetchSession();
    } catch (err) {
      console.error('Failed to sign out', err);
    }

    if (user?.phoneNumber) {
      try {
        await clearOtpCache(user.phoneNumber, false);
      } catch (err) {
        console.error('Failed to clear OTP cache on logout', err);
      }
    }

    setUserSafe(null);
  }, [user?.phoneNumber, setUserSafe, refetchSession]);

  const value: UserContextState = {
    user,
    login,
    loginWithExistingUser,
    refreshUser,
    logout,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUserContext(): UserContextState {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
}
